import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useLocalStorage } from '@uidotdev/usehooks'
import { CACHE_KEYS } from "@/common/constants";
import { useBoolean } from "@/hooks";

type Velocities = "x1" | "x2" | "x3"

type ConfigContextProps = {
  audioIsEnabled: boolean
  changeAudioStatus: () => void
  animationVelocity: Velocities
  changeAnimationVelocity: (v: Velocities) => void
  getAnimationTime: (v: Velocities) => number
  isOpenModalConfigTimingAnimation: boolean
  handleOpenModalConfigTimingAnimation(): void
  handleCloseModalConfigTimingAnimation: () => void
}

export const ConfigContext = createContext<ConfigContextProps>({} as ConfigContextProps)

type Props = {
  children: ReactNode
}

export function ConfigContextProvider(props: Props) {
  const { children } = props

  const [audioIsEnabled, setAudioIsEnabled] = useLocalStorage(CACHE_KEYS.AUDIO_ENABLED, true)
  const [animationVelocity, setAnimationVelocity] = useLocalStorage<Velocities>(CACHE_KEYS.PLAYER_VELOCITY, "x2")
  const [isOpenModalConfigTimingAnimation, handleOpenModalConfigTimingAnimation, handleCloseModalConfigTimingAnimation] = useBoolean()

  const getAnimationTime = useCallback((v: Velocities) => {
    switch (v) {
      case "x1":
        return 400
      case "x2":
        return 400
      case "x3":
        return 400
    }
  }, [])

  const changeAudioStatus = useCallback(() => {
    setAudioIsEnabled(!audioIsEnabled)
  }, [audioIsEnabled])
  const changeAnimationVelocity = useCallback((v: Velocities) => {
    setAnimationVelocity(v)
  }, [])

  const injection = useMemo(() => ({
    getAnimationTime,
    audioIsEnabled,
    changeAnimationVelocity,
    changeAudioStatus,
    animationVelocity,
    isOpenModalConfigTimingAnimation, handleOpenModalConfigTimingAnimation, handleCloseModalConfigTimingAnimation
  }), [
    getAnimationTime,
    changeAnimationVelocity,
    changeAudioStatus,
    audioIsEnabled,
    animationVelocity,
    isOpenModalConfigTimingAnimation, handleOpenModalConfigTimingAnimation, handleCloseModalConfigTimingAnimation
  ])

  return (
    <ConfigContext.Provider value={injection}>
      {children}
    </ConfigContext.Provider>
  )
}


export function useConfigContext() {
  return useContext(ConfigContext)
}